import {
  framePlacement,
  getChrome,
  getGlobalElements,
  getPages,
  getSurfaceCard,
  getWidgets,
  paletteIndexByName,
} from "../contract/document.js";
import {
  parseChromeKey,
  parseElementKey,
  parseStateKey,
  parseWidgetTreeKey,
} from "./keys.js";
import { findPhase } from "./phase-node.js";

export interface DocumentPathContext {
  activeSurface: string;
  /** Same semantics as bootstrap `getWidgetGroupScreenId`. */
  getWidgetGroupScreenId: (widgetName: string, spec: Record<string, unknown>) => string | null;
  /** Active preview phase id for a widget, or null when the base view is shown. */
  phaseIdForWidget?: (widgetName: string) => string | null;
}

function encodePointerSegment(segment: string): string {
  return segment.replace(/~/g, "~0").replace(/\//g, "~1");
}

function joinPointer(...segments: string[]): string {
  return `/${segments.map(encodePointerSegment).join("/")}`;
}

function appendItemIndices(base: string, indices: number[]): string {
  let path = base;
  for (const index of indices) {
    path += `/items/${index}`;
  }
  return path;
}

function widgetVisualBasePointer(
  _spec: Record<string, unknown>,
  widgetName: string,
): string {
  return joinPointer("widgets", widgetName, "view");
}

/** View path until the phase container, then `phases[n].items`. */
function widgetVisualPointer(
  spec: Record<string, unknown>,
  widgetName: string,
  indices: number[],
  phaseId: string | null,
): string {
  const match = findPhase(spec, phaseId);
  let pointer = widgetVisualBasePointer(spec, widgetName);
  let node = spec.view && typeof spec.view === "object"
    ? spec.view as Record<string, unknown>
    : null;
  let inPhase = false;
  for (const index of indices) {
    const usePhase = !inPhase && match != null && node?.id === match.entry.container;
    if (usePhase) {
      pointer = joinPointer("widgets", widgetName, "phases", String(match.index), "items", String(index));
      const items = match.entry.items;
      const child = Array.isArray(items) ? items[index] : null;
      node = child && typeof child === "object" ? child as Record<string, unknown> : null;
      inPhase = true;
      continue;
    }
    pointer += `/items/${index}`;
    const items = node?.items;
    const child = Array.isArray(items) ? items[index] : null;
    node = child && typeof child === "object" ? child as Record<string, unknown> : null;
  }
  return pointer;
}

function widgetPath(key: string, document: Record<string, unknown>, ctx: DocumentPathContext): string | null {
  const parsed = parseWidgetTreeKey(key);
  if (!parsed?.name) return null;
  const spec = getWidgets(document)[parsed.name] as Record<string, unknown> | undefined;
  if (!spec) return null;

  if (!parsed.section) {
    return joinPointer("widgets", parsed.name);
  }

  if (parsed.section === "visual") {
    const phaseId = ctx.phaseIdForWidget?.(parsed.name) ?? null;
    return widgetVisualPointer(spec, parsed.name, parsed.indices, phaseId);
  }

  if (parsed.section === "elements") {
    return joinPointer("widgets", parsed.name, "elements");
  }

  return null;
}

function chromePath(key: string, document: Record<string, unknown>): string | null {
  const parsed = parseChromeKey(key);
  if (!parsed?.part) return null;
  if (parsed.pageKey) {
    return joinPointer("chrome", "pages", parsed.pageKey);
  }
  if (parsed.part === "pages") {
    return joinPointer("chrome", "pages");
  }
  if (parsed.part === "frame") {
    if (parsed.frameRef) {
      const placed = framePlacement(document, parsed.frameRef);
      return placed ? joinPointer("chrome", ...placed.path) : null;
    }
    const chrome = getChrome(document);
    return Array.isArray(chrome.frame) ? joinPointer("chrome", "frame") : null;
  }
  getChrome(document);
  return joinPointer("chrome", parsed.part);
}

function elementPath(key: string, document: Record<string, unknown>): string | null {
  const parsed = parseElementKey(key);
  if (!parsed) return null;
  let base: string;
  if (parsed.scope === "global") {
    getGlobalElements(document);
    const index = paletteIndexByName(document, parsed.name);
    base = index >= 0 ? joinPointer("elements", String(index)) : joinPointer("elements", parsed.name);
  } else if (getPages(document)[parsed.scope]) {
    base = joinPointer("chrome", "pages", parsed.scope, "view");
  } else {
    base = joinPointer("chrome", parsed.scope);
  }
  return parsed.indices.length ? appendItemIndices(base, parsed.indices) : base;
}

function surfaceCardPointer(document: Record<string, unknown>, surface: string): string | null {
  if (getPages(document)[surface]) {
    return joinPointer("chrome", "pages", surface);
  }
  if (getWidgets(document)[surface]) {
    return joinPointer("widgets", surface, "view");
  }
  return null;
}

function statePath(key: string, document: Record<string, unknown>, ctx: DocumentPathContext): string | null {
  const stateKey = parseStateKey(key);
  if (!stateKey) return null;
  const surface = stateKey.surface ?? ctx.activeSurface;

  const base = surfaceCardPointer(document, surface);
  if (!base) return null;

  const page = getPages(document)[surface];
  const rootPtr = page ? `${base}/view` : base;

  if (!stateKey.indices.length) {
    return rootPtr;
  }
  return appendItemIndices(rootPtr, stateKey.indices);
}

/**
 * JSON Pointer for PATCH `set` — mirrors where `setNodeByKey` writes in the in-memory document.
 */
export function documentPathForKey(
  key: string | null | undefined,
  document: Record<string, unknown> | null | undefined,
  ctx: DocumentPathContext,
): string | null {
  if (!key || !document) return null;
  if (key === "dataset") return "/dataset";
  if (key.startsWith("dataset:")) {
    const parts = key.slice("dataset:".length).split("/").filter(Boolean);
    return joinPointer("dataset", ...parts);
  }
  if (key.startsWith("widget:")) return widgetPath(key, document, ctx);
  if (key.startsWith("chrome:")) return chromePath(key, document);
  if (key.startsWith("element:")) return elementPath(key, document);
  return statePath(key, document, ctx);
}
